import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  LuArrowLeft,
  LuClock,
  LuFileText,
  LuMessageCircle,
  LuShoppingCart,
  LuTruck,
} from "react-icons/lu";
import { RingLoader } from "react-spinners";
import { useSingleProductMutation } from "../../Redux/Api/priceAPI";
import { useAddRecentProductMutation } from "../../Redux/Api/ProductAPI";
import { RootState } from "../../Redux/store";
import { chawlaispat } from "../../Redux/apiRoutes";
import usePageMeta from "../../common/hooks/usePageMeta";
import useJsonLd from "../../common/hooks/useJsonLd";
import { breadcrumbSchema } from "../../common/seo/schema";
import { categoryPath } from "../../utils/categoryPath";
import QuotationForm from "./Components/QuotationForm";
import PopularProducts from "../Components/PopularProducts";
import { addToCart, selectCartLines, setQuantity } from "../../Redux/reducer/cartReducer";
import { ORDERING_ENABLED } from "../../config/features";
import { exGst, inclGst, DEFAULT_GST_PERCENT } from "../../utils/priceText";
import { CiButton, CiTag, QtyStepper, RateLine, inrText } from "../../common/ci/Ui";
import { useCiT } from "../../common/ci/i18n";
import { whatsappLink } from "../../config/contact";
import defaultImage from "../../assets/defaultImage.svg";

interface KV {
  key?: string;
  value?: string;
}

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/**
 * Product page — design canvas "3 · Product page".
 * Left: gallery · middle: naam, brand, spec table, description · right: sticky buy box
 * (rate ex-GST + GST, qty stepper, live total, Add to cart, WhatsApp quote, delivery note).
 * Data wahi purana /global/getPriceCalculationData/:id (id = companyProductId).
 */
const SingleProduct = () => {
  const { id = "" } = useParams();
  const t = useCiT();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userDetails = useSelector((state: RootState) => state.Login.ContractorData);
  const lines = useSelector(selectCartLines);
  const [singleProductMutation, { data: singleProduct, isLoading }] = useSingleProductMutation();
  const [addRecentProduct] = useAddRecentProductMutation();
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const inCart = lines.find((l) => l.companyProductId === id);
  const [qty, setQty] = useState<number>(inCart ? inCart.quantity : 1);

  useEffect(() => {
    if (id) {
      singleProductMutation(id);
      setImgIdx(0);
      // Route wahi rehta hai (/product/:id/:name), sirf param badalta hai — is
      // liye component remount nahi hota aur qty pichhle product ki reh jaati
      // thi. 200 kg TMT dekh kar doosre product par jaane par "Add to cart"
      // 200 unit galat item ke daal deta tha.
      const existing = lines.find((l) => l.companyProductId === id);
      setQty(existing ? existing.quantity : 1);
      if (userDetails) addRecentProduct({ contractorId: userDetails._id, productId: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const p: any = singleProduct || {};
  const name: string = p.productName || "";
  const images: string[] = Array.isArray(p.productImage) ? p.productImage.filter(Boolean) : [];
  const unit: string = p.priceStructureUnit || p.unit || "unit";
  const gst = Number.isFinite(Number(p.gstPercent)) ? Number(p.gstPercent) : DEFAULT_GST_PERCENT;
  const rate = useMemo(() => {
    if (Number(p.taxableValue) > 0) return Number(p.taxableValue);
    const low = p.highLowPrice?.lowFinalPrice ?? p.finalPrice;
    const ex = exGst(low, gst);
    return Number.isFinite(ex) && ex > 0 ? ex : null;
  }, [p.taxableValue, p.highLowPrice, p.finalPrice, gst]);

  const lineTaxable = rate ? round2(rate * (qty || 0)) : 0;
  const lineGst = round2(lineTaxable * (gst / 100));

  // companyProducts.description me aksar unit weight ("9.42 KG/MTR", "80 KG") ya
  // sirf category ka naam ("AGRI PIPE") likha hota hai. Wo paragraph ke laayak
  // nahi — weight spec table me jaata hai, baaki drop; Description tab category
  // ka text dikhata hai taaki PDP kabhi khali na rahe.
  const rawDesc: string = String(p.companyProductDescription || "").trim();
  const isUnitWeight = /^[\d.,]+\s*(kg|gm|mt|ton)\b/i.test(rawDesc) && rawDesc.length <= 25;
  const unitWeight: string = isUnitWeight ? rawDesc : "";
  const desc: string =
    (rawDesc.length >= 25 && !isUnitWeight ? rawDesc : "") || String(p.categoryDescription || "").trim();

  // Title = wahi jo log search karte hain: "<naam> rate today rudrapur".
  // canonical apne aap current path se banta hai (usePageMeta) — pehle yahan
  // `${chawlaispat}/product/${id}` bhejne se canonical do baar path jod deta tha.
  usePageMeta(
    name ? `${name} Rate Today in Rudrapur | Chawla Ispat` : "Product | Chawla Ispat",
    name
      ? `${name}${p.companyName ? ` (${p.companyName})` : ""} ka aaj ka rate (GST sahit). Rudrapur godown se 1-2 din me delivery, GST invoice ke saath. Online order ya WhatsApp par quote.`
      : "Chawla Ispat - Rudrapur ka building material bazaar.",
    chawlaispat,
    {
      ogType: "product",
      image: images[0],
      // Canonical = bilkul wahi URL jo sitemap me hai. Naam ka encoding/case
      // alag ho to bhi Google ke liye ek hi page rahega.
      canonical: name ? `${chawlaispat}/product/${id}/${encodeURIComponent(name)}` : undefined,
    }
  );

  const jsonLd = useMemo(
    () =>
      name && rate
        ? {
            "@context": "https://schema.org",
            "@type": "Product",
            name,
            sku: p.itemCode ? String(p.itemCode) : undefined,
            image: images.length ? images : undefined,
            brand: p.companyName ? { "@type": "Brand", name: p.companyName } : undefined,
            category: p.categoryName || undefined,
            description: desc || undefined,
            offers: {
              "@type": "Offer",
              url: `${chawlaispat}/product/${id}/${encodeURIComponent(name)}`,
              priceCurrency: "INR",
              price: inclGst(rate, gst),
              // Steel ka rate roz badalta hai; iske bina Google Merchant
              // "price may be out of date" flag karta hai.
              priceValidUntil: new Date(Date.now() + 24 * 60 * 60 * 1000)
                .toISOString()
                .slice(0, 10),
              priceSpecification: {
                "@type": "UnitPriceSpecification",
                price: inclGst(rate, gst),
                priceCurrency: "INR",
                valueAddedTaxIncluded: true,
                unitText: unit,
              },
              availability: p.status ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
              seller: { "@type": "Organization", name: "Chawla Ispat Pvt. Ltd." },
            },
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, rate, gst, p.itemCode, p.companyName, p.categoryName, desc, p.status, images.join("|"), id, unit]
  );
  useJsonLd("product", jsonLd);

  // Google result me "chawlaispat.com > TMT Bar > Kamdhenu 12mm" dikhta hai.
  const crumbs = useMemo(() => {
    if (!name) return null;
    const list: { name: string; path: string }[] = [{ name: "Home", path: "/" }];
    if (p.categoryName) {
      list.push({ name: String(p.categoryName), path: categoryPath({ name: String(p.categoryName) }) });
    }
    list.push({ name, path: `/product/${id}/${encodeURIComponent(name)}` });
    return breadcrumbSchema(list);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, p.categoryName, id]);
  useJsonLd("breadcrumb", crumbs);

  const specs: [string, string][] = (
    [
      [t("Brand / company"), p.companyName],
      [t("Item code"), p.itemCode],
      [t("Sold in"), unit],
      [t("Unit weight"), unitWeight],
      [t("Category"), p.categoryName],
      ...(Array.isArray(p.categoryProductDescriptionLabels)
        ? (p.categoryProductDescriptionLabels as KV[]).map((kv) => [kv.key || "", kv.value || ""])
        : []),
      ...(Array.isArray(p.companyProductDescriptionLabels)
        ? (p.companyProductDescriptionLabels as KV[]).map((kv) => [kv.key || "", kv.value || ""])
        : []),
    ] as [string, any][]
  )
    .filter(([k, v]) => k && v !== undefined && v !== null && String(v).trim() !== "")
    .map(([k, v]) => [k, String(v)] as [string, string]);

  // Category level facts (about / use case / other name / thickness range...) —
  // ye har product par same rehte hain, isliye spec table ke neeche alag block me.
  const categoryFacts: [string, string][] = (
    Array.isArray(p.categoryDescriptionLabels) ? (p.categoryDescriptionLabels as KV[]) : []
  )
    .map((kv) => [String(kv.key || "").trim(), String(kv.value || "").trim()] as [string, string])
    .filter(([k, v]) => k !== "" && v !== "");

  const add = () => {
    if (!id) return;
    if (inCart) {
      dispatch(setQuantity({ companyProductId: id, quantity: qty }));
      toast.success(t("Cart update ho gaya"));
    } else {
      dispatch(
        addToCart({
          companyProductId: id,
          quantity: qty > 0 ? qty : 1,
          companyId: p.companyId,
          itemName: name,
          companyName: p.companyName,
          itemCode: p.itemCode !== undefined && p.itemCode !== null ? String(p.itemCode) : undefined,
          unit,
          image: images[0],
        })
      );
      toast.success(t("Cart me add ho gaya"));
    }
  };

  const waText = `Namaste, ${name}${p.companyName ? ` (${p.companyName})` : ""} — ${qty} ${unit} ka rate chahiye. ${window.location.href}`;

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center bg-ci-bg">
        <RingLoader color="#182A70" size={90} />
      </div>
    );
  }

  return (
    <div className="bg-ci-bg pb-10">
      <QuotationForm isOpen={isOpenModal} onClose={() => setIsOpenModal(false)} productId={id} unitId={p.unitId} />

      <div className="mx-auto max-w-[1280px] px-4 pt-4 sm:px-5 sm:pt-5">
        <button type="button" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-[13px] font-semibold text-ci-navy">
          <LuArrowLeft size={16} /> {t("Back")}
        </button>
        {/* Breadcrumb — crawler ko category tak asli <a href> chahiye, isliye plain text nahi */}
        <nav aria-label="Breadcrumb" className="mt-1 truncate text-[13px] text-ci-muted">
          <Link to="/" className="hover:underline">{t("Home")}</Link>
          {p.categoryName ? (
            <>
              {" › "}
              <Link
                to={categoryPath({ name: String(p.categoryName) })}
                className="hover:underline"
              >
                {String(p.categoryName)}
              </Link>
            </>
          ) : null}
          {p.companyName ? <>{" › "}{String(p.companyName)}</> : null}
          {" › "}
          <span aria-current="page">{name}</span>
        </nav>
      </div>

      <div className="mx-auto mt-4 grid max-w-[1280px] grid-cols-1 gap-6 px-4 sm:px-5 lg:grid-cols-[400px_minmax(0,1fr)_340px] lg:items-start">
        {/* gallery */}
        <div className="flex flex-col gap-3">
          <div className="aspect-[4/3] w-full overflow-hidden rounded-lg border border-ci-line bg-white">
            <img src={images[imgIdx] || defaultImage} alt={name} className="h-full w-full object-contain" />
          </div>
          {images.length > 1 ? (
            <div className="grid grid-cols-4 gap-2.5">
              {images.slice(0, 8).map((src, i) => (
                <button
                  key={src + i}
                  type="button"
                  onClick={() => setImgIdx(i)}
                  className={`aspect-[4/3] overflow-hidden rounded-md border-2 bg-white ${i === imgIdx ? "border-ci-navy" : "border-ci-line"}`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          ) : null}
        </div>

        {/* info */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            {p.status ? <CiTag tone="ok">{t("Available")} · {t("Rudrapur godown")}</CiTag> : <CiTag tone="muted">{t("Abhi stock me nahi")}</CiTag>}
            {p.itemCode ? <CiTag tone="navy">Code {p.itemCode}</CiTag> : null}
          </div>
          <h1 className="font-display text-[30px] font-bold uppercase leading-[1.05] text-ci-navy sm:text-[38px]">{name}</h1>
          {p.companyName ? (
            <span className="text-[15px] text-ci-muted">
              {t("Brand")}: <b className="text-ci-ink">{p.companyName}</b> · {t("sold in")} <b className="text-ci-ink">{unit}</b>
            </span>
          ) : null}

          {/* mobile buy strip (desktop has the sticky box) */}
          <div className="flex flex-col gap-3 rounded-lg border border-ci-line bg-white p-4 lg:hidden">
            <RateLine rate={rate} unit={unit} gstPercent={gst} size="lg" />
            {ORDERING_ENABLED ? (
              <>
                <QtyStepper size="lg" value={qty} unit={unit} min={0} onChange={setQty} />
                {rate ? (
                  <div className="flex justify-between text-[14px]">
                    <span className="text-ci-muted">{t("Total incl. GST")}</span>
                    <b className="ci-num">{inrText(lineTaxable + lineGst)}</b>
                  </div>
                ) : null}
              </>
            ) : null}
          </div>

          {specs.length ? (
            <div className="overflow-hidden rounded-lg border border-ci-line bg-white">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="text-left text-[12px] uppercase tracking-wide text-ci-muted">
                    <th className="px-4 py-2.5">{t("Specification")}</th>
                    <th className="px-4 py-2.5">{t("Value")}</th>
                  </tr>
                </thead>
                <tbody>
                  {specs.map(([k, v], i) => (
                    <tr key={k + i} className="border-t border-ci-line">
                      <td className="w-[40%] px-4 py-2.5 text-ci-muted">{k}</td>
                      <td className="px-4 py-2.5 font-semibold">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {desc ? (
            <div className="flex flex-col gap-1.5">
              <b className="text-[15px]">{t("Description")}</b>
              <p className="whitespace-pre-line text-[14px] leading-relaxed text-ci-muted">{desc}</p>
            </div>
          ) : null}

          {categoryFacts.length ? (
            <div className="flex flex-col gap-1.5">
              <b className="text-[15px]">{t("More about")} {p.categoryName}</b>
              <dl className="flex flex-col gap-1 text-[14px] leading-relaxed text-ci-muted">
                {categoryFacts.map(([k, v]) => (
                  <div key={k} className="flex flex-wrap gap-x-1.5">
                    <dt className="font-medium capitalize">{k}:</dt>
                    <dd>{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ) : null}

          {p.companyDescription ? (
            <div className="flex flex-col gap-1.5">
              <b className="text-[15px]">{t("About the brand")}{p.companyName ? ` · ${p.companyName}` : ""}</b>
              <p className="text-[14px] leading-relaxed text-ci-muted">{String(p.companyDescription)}</p>
            </div>
          ) : null}
        </div>

        {/* sticky buy box (desktop) */}
        <div className="hidden flex-col gap-4 rounded-lg border border-ci-line bg-white p-5 lg:sticky lg:top-[120px] lg:flex">
          <RateLine rate={rate} unit={unit} gstPercent={gst} size="lg" />
          <span className="flex items-center gap-1.5 text-[12px] text-ci-muted"><LuClock size={13} /> {t("Rate roz update · GST included")}</span>
          {ORDERING_ENABLED ? (
            <>
              <div className="flex flex-col gap-2">
                <b className="text-[13px]">{t("Quantity")} ({unit})</b>
                <QtyStepper size="lg" value={qty} unit={unit} min={0} onChange={setQty} />
              </div>
              {rate ? (
                <div className="flex flex-col gap-1.5 rounded-md bg-ci-bg p-3.5 text-[14px]">
                  <div className="flex justify-between text-[16px] font-bold"><span>{t("Total")} · {qty} {unit}</span><span className="ci-num">{inrText(lineTaxable + lineGst)}</span></div>
                  <div className="flex justify-between text-[12px] text-ci-muted"><span>{t("incl. GST")} {gst}%</span><span className="ci-num">{inrText(lineGst)}</span></div>
                </div>
              ) : null}
              <CiButton variant="orange" size="lg" onClick={add} disabled={!id}>
                <LuShoppingCart size={18} /> {inCart ? t("Cart update karein") : t("Add to cart")}
              </CiButton>
            </>
          ) : null}
          <a href={whatsappLink(waText)} target="_blank" rel="noreferrer">
            <CiButton variant="line" className="w-full"><LuMessageCircle size={16} /> {t("Quote for bigger qty")}</CiButton>
          </a>
          <button type="button" onClick={() => (userDetails ? setIsOpenModal(true) : navigate("/signup"))} className="text-[13px] text-ci-navy underline">
            {t("Get latest price (team se)")}
          </button>
          <div className="flex flex-col gap-2 border-t border-ci-line pt-3 text-[13px]">
            <span className="flex items-start gap-2"><LuTruck size={16} className="mt-0.5 shrink-0" /><span>{t("Delivery Rudrapur godown se, 1–2 din · pincode checkout par")}</span></span>
            <span className="flex items-start gap-2"><LuFileText size={16} className="mt-0.5 shrink-0" /><span>{t("GST invoice with order · cash / dealer credit")}</span></span>
          </div>
        </div>
      </div>

      <div className="mx-auto mt-10 max-w-[1280px] px-4 sm:px-5">
        <PopularProducts />
      </div>

      {/* mobile sticky bar */}
      {ORDERING_ENABLED ? (
        <div className="sticky bottom-[58px] z-30 flex items-center gap-3 border-t border-ci-line bg-white px-4 py-2.5 lg:hidden">
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-[12px] text-ci-muted">{qty} {unit} · {t("incl. GST")}</span>
            <b className="ci-num text-[18px]">{rate ? inrText(lineTaxable + lineGst) : t("Rate available nahi")}</b>
          </div>
          <CiButton variant="orange" onClick={add} disabled={!id}>
            <LuShoppingCart size={18} /> {inCart ? t("Update") : t("Add to cart")}
          </CiButton>
        </div>
      ) : null}
    </div>
  );
};

export default SingleProduct;
